/**
 * Utility functions for handling vILT type defaults
 * These functions ensure consistent v_ilt_type behavior across all forms
 */

import {
    ActivityLMSType,
    ActivityModality,
    ActivityViltType,
} from '../components/Activity/Common/Common';
import { ActivityData } from '../interfaces/activity';

/**
 * Determines the default v_ilt_type based on lms_type for vILT/Hybrid activities.
 *
 * For activities using WebEx-integrated LMS types (MyClass, Kiku, SkillBuilder, CustomerLMS),
 * the default v_ilt_type is WebEx. For "Other" v_ilt_type, it's preserved as-is.
 *
 * @param lmsType - The LMS type selected
 * @param currentViltType - The current v_ilt_type value (may be null/undefined)
 * @returns The appropriate v_ilt_type value
 */
export const getDefaultViltType = (
    lmsType: string | null | undefined,
    currentViltType: string | null | undefined,
): string | null => {
    // Preserve "Other" selection - user explicitly chose manual vILT configuration
    if (currentViltType === ActivityViltType.Other) {
        return ActivityViltType.Other;
    }

    // For supported LMS types, default to WebEx integration
    switch (lmsType) {
        case ActivityLMSType.MyClass:
        case ActivityLMSType.Kiku:
        case ActivityLMSType.SkillBuilder:
        case ActivityLMSType.CustomerLMS:
            return ActivityViltType.WebEx;
        default:
            return currentViltType || null;
    }
};

/**
 * Determines if an activity requires v_ilt_type to be set.
 *
 * @param modality - The activity modality
 * @param status - The activity status
 * @param lmsType - The LMS type
 * @returns true if v_ilt_type should be set
 */
export const requiresViltType = (
    modality: string | null | undefined,
    status: string | null | undefined,
    lmsType: string | null | undefined,
): boolean => {
    const isViltOrHybrid =
        modality === ActivityModality.vILT ||
        modality === ActivityModality.Hybrid;

    const isActive = status === 'Active';

    const hasLmsType = !!lmsType;

    return isViltOrHybrid && isActive && hasLmsType;
};

/**
 * Ensures v_ilt_type is properly set for the activity data before submission.
 * This function should be called in form submission handlers to ensure
 * v_ilt_type is always correctly set for vILT/Hybrid activities.
 *
 * @param activityData - The activity data being submitted
 * @returns The activity data with v_ilt_type properly defaulted
 */
export const ensureViltTypeForSubmission = <T extends Partial<ActivityData>>(
    activityData: T,
): T => {
    const { activity_modality, activity_status, lms_type, v_ilt_type } =
        activityData;

    // Only apply defaulting for vILT/Hybrid activities
    const isViltOrHybrid =
        activity_modality === ActivityModality.vILT ||
        activity_modality === ActivityModality.Hybrid;

    if (!isViltOrHybrid) {
        return activityData;
    }

    // Get the appropriate v_ilt_type value
    const defaultedViltType = getDefaultViltType(lms_type, v_ilt_type);

    // Only update if we have a value to set and it's different
    if (defaultedViltType && defaultedViltType !== v_ilt_type) {
        return {
            ...activityData,
            v_ilt_type: defaultedViltType,
        };
    }

    return activityData;
};
